import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  agreementCreateSchema,
  agreementUpdateSchema,
  type AgreementSummary,
  type AgreementCreateInput,
  type AgreementUpdateInput,
} from '@ventin/shared';
import { useCreateAgreement, useUpdateAgreement } from '@/lib/adminApi';

type Mode = 'create' | 'edit';

interface Props {
  mode: Mode;
  /** Required when mode==='create'. Ignored in edit mode (companyId comes from `initial`). */
  companyId?: string;
  initial?: AgreementSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FieldErrors {
  includedIncrements?: string;
  customRate?: string;
  cycleDay?: string;
  startDate?: string;
  endDate?: string;
  gaPropertyId?: string;
  legacyPlanRef?: string;
  form?: string;
}

/**
 * AgreementFormDialog — create/edit a billing Agreement (AGR-01..04 + SET-05).
 *
 * Create mode submits via `useCreateAgreement` — the API (Plan 02-04) translates
 * this into deactivate-then-create inside a single Prisma transaction, so the
 * "new Agreement replaces the active one" semantics is server-side. The dialog
 * surfaces that intent with a warning line and a submit button labelled
 * "Create and deactivate current" so the admin is never surprised.
 *
 * Edit mode is in-place — adjusts `gaPropertyId` (SET-05 override), `endDate`,
 * or any other mutable field via `useUpdateAgreement`. `isActive=false` is NOT
 * exposed as a form field (deactivation is its own CTA on the active card).
 *
 * AGR-04 discipline: NO rollover / carryOver / carriedIncrements field. The
 * AgreementsPage.test.tsx harness asserts zero rollover-named inputs exist.
 * If you are tempted to add one, read `packages/shared/src/schemas/agreement.ts`
 * first — the contract rejects it at parse time.
 */
export function AgreementFormDialog({ mode, companyId, initial, open, onOpenChange }: Props) {
  const [includedIncrements, setIncludedIncrements] = useState('');
  const [customRate, setCustomRate] = useState('');
  const [cycleDay, setCycleDay] = useState('27');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [gaPropertyId, setGaPropertyId] = useState('');
  const [legacyPlanRef, setLegacyPlanRef] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  const createMut = useCreateAgreement();
  const updateMut = useUpdateAgreement();

  // Re-sync form state whenever the dialog opens (or the initial target changes).
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (mode === 'edit' && initial) {
      setIncludedIncrements(String(initial.includedIncrements));
      setCustomRate(initial.customRate);
      setCycleDay(String(initial.cycleDay));
      setStartDate(isoToDateInput(initial.startDate));
      setEndDate(initial.endDate ? isoToDateInput(initial.endDate) : '');
      setGaPropertyId(initial.gaPropertyId ?? '');
      setLegacyPlanRef(initial.legacyPlanRef ?? '');
    } else {
      setIncludedIncrements('');
      setCustomRate('');
      setCycleDay('27');
      setStartDate('');
      setEndDate('');
      setGaPropertyId('');
      setLegacyPlanRef('');
    }
  }, [open, mode, initial]);

  function buildCreatePayload(): { input?: AgreementCreateInput; errs: FieldErrors } {
    const raw: Record<string, unknown> = {
      includedIncrements: includedIncrements === '' ? NaN : Number(includedIncrements),
      customRate: customRate.trim(),
      cycleDay: cycleDay === '' ? NaN : Number(cycleDay),
      startDate: dateInputToIso(startDate),
    };
    if (gaPropertyId.trim()) raw.gaPropertyId = gaPropertyId.trim();
    if (legacyPlanRef.trim()) raw.legacyPlanRef = legacyPlanRef.trim();

    const parsed = agreementCreateSchema.safeParse(raw);
    if (parsed.success) return { input: parsed.data, errs: {} };
    return { errs: flattenIssues(parsed.error.issues) };
  }

  function buildUpdatePayload(): { input?: AgreementUpdateInput; errs: FieldErrors } {
    const raw: Record<string, unknown> = {};
    if (includedIncrements !== '') raw.includedIncrements = Number(includedIncrements);
    if (customRate.trim()) raw.customRate = customRate.trim();
    if (cycleDay !== '') raw.cycleDay = Number(cycleDay);
    if (startDate) raw.startDate = dateInputToIso(startDate);
    if (endDate) raw.endDate = dateInputToIso(endDate);
    if (gaPropertyId.trim()) raw.gaPropertyId = gaPropertyId.trim();
    if (legacyPlanRef.trim()) raw.legacyPlanRef = legacyPlanRef.trim();

    const parsed = agreementUpdateSchema.safeParse(raw);
    if (parsed.success) return { input: parsed.data, errs: {} };
    return { errs: flattenIssues(parsed.error.issues) };
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (mode === 'create') {
      if (!companyId) {
        setErrors({ form: 'Missing companyId — pick a company first.' });
        return;
      }
      const { input, errs } = buildCreatePayload();
      if (!input) {
        setErrors(errs);
        return;
      }
      setErrors({});
      setBusy(true);
      try {
        await createMut.mutateAsync({ companyId, input });
        toast.success('Saved');
        onOpenChange(false);
      } catch {
        setErrors({ form: "Couldn't save — try again" });
        toast.error("Couldn't save — try again");
      } finally {
        setBusy(false);
      }
      return;
    }

    // edit
    if (!initial) return;
    const { input, errs } = buildUpdatePayload();
    if (!input) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await updateMut.mutateAsync({ id: initial.id, patch: input });
      toast.success('Saved');
      onOpenChange(false);
    } catch {
      setErrors({ form: "Couldn't save — try again" });
      toast.error("Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New Agreement' : 'Edit Agreement'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'This will deactivate the current Agreement effective the new start date.'
              : 'Adjust this Agreement in place. GA4 property ID overrides the Company default.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="includedIncrements">Included increments</Label>
            <Input
              id="includedIncrements"
              name="includedIncrements"
              type="number"
              min={0}
              step={1}
              required={mode === 'create'}
              value={includedIncrements}
              onChange={(e) => setIncludedIncrements(e.target.value)}
              aria-invalid={!!errors.includedIncrements}
            />
            {errors.includedIncrements && (
              <p className="text-xs text-destructive">{errors.includedIncrements}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="customRate">Custom rate (USD per increment)</Label>
            <Input
              id="customRate"
              name="customRate"
              type="number"
              min={0}
              step="0.01"
              required={mode === 'create'}
              value={customRate}
              onChange={(e) => setCustomRate(e.target.value)}
              aria-invalid={!!errors.customRate}
            />
            {errors.customRate && <p className="text-xs text-destructive">{errors.customRate}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="cycleDay">Cycle day (1–28, default 27)</Label>
            <Input
              id="cycleDay"
              name="cycleDay"
              type="number"
              min={1}
              max={28}
              step={1}
              required={mode === 'create'}
              value={cycleDay}
              onChange={(e) => setCycleDay(e.target.value)}
              aria-invalid={!!errors.cycleDay}
            />
            {errors.cycleDay && <p className="text-xs text-destructive">{errors.cycleDay}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="startDate">Start date</Label>
            <Input
              id="startDate"
              name="startDate"
              type="date"
              required={mode === 'create'}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              aria-invalid={!!errors.startDate}
            />
            {errors.startDate && <p className="text-xs text-destructive">{errors.startDate}</p>}
          </div>

          {mode === 'edit' && (
            <div className="space-y-2">
              <Label htmlFor="endDate">End date (optional)</Label>
              <Input
                id="endDate"
                name="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                aria-invalid={!!errors.endDate}
              />
              {errors.endDate && <p className="text-xs text-destructive">{errors.endDate}</p>}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="gaPropertyId">GA4 property ID (overrides Company default)</Label>
            <Input
              id="gaPropertyId"
              name="gaPropertyId"
              type="text"
              placeholder="e.g. 312345678"
              value={gaPropertyId}
              onChange={(e) => setGaPropertyId(e.target.value)}
              aria-invalid={!!errors.gaPropertyId}
            />
            {errors.gaPropertyId && (
              <p className="text-xs text-destructive">{errors.gaPropertyId}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="legacyPlanRef">Legacy plan reference (optional)</Label>
            <Input
              id="legacyPlanRef"
              name="legacyPlanRef"
              type="text"
              maxLength={80}
              value={legacyPlanRef}
              onChange={(e) => setLegacyPlanRef(e.target.value)}
              aria-invalid={!!errors.legacyPlanRef}
            />
            {errors.legacyPlanRef && (
              <p className="text-xs text-destructive">{errors.legacyPlanRef}</p>
            )}
          </div>

          {errors.form && (
            <Alert variant="destructive">
              <AlertDescription>{errors.form}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy
                ? 'Saving…'
                : mode === 'create'
                  ? 'Create and deactivate current'
                  : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- helpers ----------

/** Convert an ISO date-time string to a `YYYY-MM-DD` string for <input type="date">. */
function isoToDateInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Convert a `YYYY-MM-DD` date input to an ISO UTC date-time string (midnight). */
function dateInputToIso(dateStr: string): string {
  if (!dateStr) return '';
  // Treat as midnight UTC so the API's ISO datetime schema accepts it.
  return `${dateStr}T00:00:00.000Z`;
}

function flattenIssues(issues: { path: (string | number)[]; message: string }[]): FieldErrors {
  const errs: FieldErrors = {};
  for (const issue of issues) {
    const key = issue.path[0] as keyof FieldErrors | undefined;
    if (key && !errs[key]) errs[key] = issue.message;
  }
  return errs;
}
